import type { Dispatch, SetStateAction } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';

import { isKeyOfObject } from '../../../../helpers/utils';
import { useInputState } from '../../shared/hooks';
import type {
  CheckBoxRadioState,
  SharedCheckboxRadioGroupProps,
  SharedSingleCheckboxRadioProps,
} from './types';

const { radioCheckbox } = tokens;

/**
 * Provides the true state for the checkbox or radio
 * Allows us a way to combine in "selected" appropriately from checked to get the true state of the checkbox or radio
 */
export const useCheckBoxRadioState = ({
  disabled,
  errorText,
  invalid,
  onFocus,
  onBlur,
  checked,
}: Pick<
  SharedCheckboxRadioGroupProps,
  'disabled' | 'errorText' | 'invalid' | 'onFocus' | 'onBlur'
> & {
  /**
   * Whether the checkbox or radio is checked
   */
  checked: boolean | 'indeterminate';
}): {
  /**
   * Sets the focus state for the checkbox or radio
   */
  setFocused: Dispatch<SetStateAction<boolean>>;
  /**
   * The new checkbox/radio row state - adds "selected" (when not focused/invalid)
   */
  handleBlur: SharedCheckboxRadioGroupProps['onFocus'];
  /**
   * The new checkbox/radio row state - adds "selected" (when not focused/invalid)
   */
  handleFocus: SharedCheckboxRadioGroupProps['onBlur'];
  /**
   * The new checkbox/radio row state - adds "selected" (when not focused/invalid)
   */
  rowState: CheckBoxRadioState;
  /**
   * The new checkbox or radio state (should stay "checked" on focus)
   */
  buttonState: CheckBoxRadioState;
} => {
  const { state, handleFocus, handleBlur, setFocused } = useInputState({
    disabled,
    errorText,
    invalid,
    onFocus,
    onBlur,
  });

  // adds the "selected" state to the checkbox or radio so we can use it in the row styling
  const rowState: CheckBoxRadioState = state
    ? state
    : checked && checked !== 'indeterminate'
      ? 'selected'
      : undefined;

  // ensures the checkbox or radio stays "checked" when focused
  const buttonState: CheckBoxRadioState = state === 'focused' && checked ? 'selected' : rowState;

  return { rowState, buttonState, handleFocus, handleBlur, setFocused };
};

/**
 * This function allows us to get radio/checkbox row styling
 * This ensures the checkbox/radio row styling is consistent and changes only need to be made in one place
 * We need to use this "translator" because the tamagui radio group does not allow us to use our own sizing, tokens etc. (no headless mode)
 */
export const getCustomRadioCheckboxRowStyling = ({
  size,
  state,
}: Pick<SharedSingleCheckboxRadioProps, 'size' | 'state'>) => {
  const isXSmall = size === 'xsmall';
  const disabled = state === 'inactive';
  const focused = state === 'focused';

  // Define hover and focus styles
  const hoverStyle = !disabled
    ? {
        borderColor: !isXSmall
          ? radioCheckbox.color.border.row.state.hover
          : ('$transparent' as Color),
        backgroundColor: isXSmall
          ? radioCheckbox.color.background.element.row.state.hover
          : ('$transparent' as Color),
      }
    : {};
  const focusStyle =
    focused && !disabled
      ? {
          borderColor: radioCheckbox.color.border.row.state.focused,
          borderWidth: radioCheckbox.borderWidth.default,
        }
      : {};

  const cursor = state === 'inactive' ? 'default' : 'pointer';
  const borderColor = isXSmall
    ? undefined
    : isKeyOfObject(state, radioCheckbox.color.border.row.state)
      ? radioCheckbox.color.border.row.state[state]
      : radioCheckbox.color.border.row.default;

  return {
    backgroundColor: '$transparent' as Color,
    height: isKeyOfObject(size, radioCheckbox.size.rowHeight)
      ? radioCheckbox.size.rowHeight[size]
      : 0,
    paddingRight: isKeyOfObject(size, radioCheckbox.padding.leftRight)
      ? radioCheckbox.padding.leftRight[size]
      : 0,
    paddingLeft: isKeyOfObject(size, radioCheckbox.padding.leftRight)
      ? radioCheckbox.padding.leftRight[size]
      : 0,
    borderRadius: isKeyOfObject(size, radioCheckbox.cornerRadius.row)
      ? radioCheckbox.cornerRadius.row[size]
      : 0,
    borderWidth: radioCheckbox.borderWidth.default,
    // hides the border if it's xsmall
    borderColor: borderColor || ('$transparent' as Color),
    hoverStyle,
    focusStyle,
    cursor,
  };
};
